import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import CookieConsent from './components/CookieConsent';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import SeoTags from './components/SeoTags';
import { ToastProvider } from './components/Toast';
import ErrorBoundary from './components/ErrorBoundary';
import { clearAllRaceData } from './services/csvParser';
import './index.css';

const CLERK_PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

// Lazy load all route components for code splitting
const LandingPage = lazy(() => import('./pages/LandingPage'));
const RaceAnalyzer = lazy(() => import('./pages/RaceAnalyzer'));
const SignUpPage = lazy(() => import('./pages/SignUpPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));

// Loading fallback component
function LoadingFallback() {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: '#F8F5F0'
    }}>
      <div style={{
        textAlign: 'center'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid #E5E7EB',
          borderTop: '4px solid #2B634B',
          borderRadius: '50%',
          margin: '0 auto 16px',
          animation: 'spin 0.8s linear infinite'
        }}></div>
        <p style={{
          color: '#2B634B',
          fontSize: '16px',
          fontWeight: '600'
        }}>Loading...</p>
      </div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// Any address that isn't a page: say so, with a way back, instead of a blank screen.
function NotFound() {
  return (
    <main style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--cream)', padding: '24px 16px' }}>
      <div style={{ textAlign: 'center', maxWidth: '440px' }}>
        <h1 style={{ fontSize: 'clamp(28px, 6vw, 40px)', color: 'var(--forest-green)', margin: '0 0 12px' }}>Page not found</h1>
        <p style={{ color: 'var(--text-mid)', fontSize: '16px', lineHeight: 1.6, margin: '0 0 24px' }}>
          The page you're looking for doesn't exist or has moved.
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <a href="/" className="btn btn-primary">Go to Home</a>
          <a href="/pricing" className="btn btn-secondary">See Pricing</a>
        </div>
      </div>
    </main>
  );
}

// Cards, rankings and race entries saved on this device belong to whoever
// was signed in. They go when that person signs out (any sign-out button, or
// the session ending) and when a different account signs in, so the next
// person on a shared phone or computer never sees them. Small settings
// (cookie choice, labels seen) stay.
const ACCOUNT_KEY = 'ffAccount';
const ACCOUNT_DATA_KEYS = ['ffRaceChanges', 'ffLastView', 'rankRequestTimes'];

function ClearOnSignOut() {
  const { isLoaded, userId } = useAuth();
  useEffect(() => {
    if (!isLoaded) return;
    let last = null;
    try { last = window.localStorage.getItem(ACCOUNT_KEY); } catch { return; }
    if (userId && userId === last) return;
    if (last) {
      clearAllRaceData();
      ACCOUNT_DATA_KEYS.forEach(k => { try { window.localStorage.removeItem(k); } catch { /* blocked */ } });
    }
    try {
      if (userId) window.localStorage.setItem(ACCOUNT_KEY, userId);
      else window.localStorage.removeItem(ACCOUNT_KEY);
    } catch { /* storage blocked: nothing was saved to clear */ }
  }, [isLoaded, userId]);
  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
        <ClearOnSignOut />
        <ToastProvider>
          <Router>
            <ScrollToTop />
            <SeoTags />
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <RaceAnalyzer />
                    </ProtectedRoute>
                  }
                />
                <Route path="/signup" element={<SignUpPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <CookieConsent />
          </Router>
        </ToastProvider>
      </ClerkProvider>
    </ErrorBoundary>
  );
}

export default App;
